export const ROLE_LABELS = {
  admin: "Administrator",
  admin_addl: "Admin_addl",
  manager: "Manager",
  purchase_manager: "Purchase Manager",
  admin_manager: "Admin Manager",
  accounts_manager: "Accounts Manager",
  accounts_assistant: "Accounts Assistant",
  stores_manager: "Stores Manager",
  stores_incharge: "Stores Incharge",
  maintenance_manager: "Maintenance Manager",
  technician: "Technician",
  gate_keeper: "Gate Keeper",
  line_supervisor: "Line Supervisor",
  logistics_supervisor: "Logistics Supervisor",
  hr_manager: "HR Manager",
  employee: "Employee",
};

// Feb 2026 — `admin_addl` mirrors `admin` everywhere on the client so gated
// controls behave identically for both. Keeping them as two distinct roles
// lets notification triggers target one but not the other.
export const ADMIN_TIER_ROLES = ["admin", "admin_addl"];

export const isAdminRole = (role) => ADMIN_TIER_ROLES.includes(role);

export const FINANCIAL_ROLES = [...ADMIN_TIER_ROLES, "manager", "accounts_manager", "accounts_assistant"];
export const ASSET_CREATE_ROLES = [...ADMIN_TIER_ROLES, "manager", "stores_manager", "accounts_manager"];
export const MAINTENANCE_EDIT_ROLES = [...ADMIN_TIER_ROLES, "maintenance_manager", "technician", "manager", "stores_manager"];
export const USER_MGMT_ROLES = [...ADMIN_TIER_ROLES];
export const PO_RAISE_ROLES = [...ADMIN_TIER_ROLES, "manager", "purchase_manager", "admin_manager", "stores_manager", "stores_incharge"];
export const PO_APPROVE_ROLES = [...ADMIN_TIER_ROLES, "manager"];
export const SPARE_ISSUE_ROLES = [...ADMIN_TIER_ROLES, "manager", "stores_manager", "stores_incharge", "maintenance_manager"];
export const CONSUMABLE_MANAGE_ROLES = [...ADMIN_TIER_ROLES, "manager", "stores_manager", "stores_incharge"];
export const TRANSFER_ROLES = [...ADMIN_TIER_ROLES, "manager", "stores_manager"];
export const WRITEOFF_ROLES = [...ADMIN_TIER_ROLES, "manager"];
export const AUDIT_ROLES = [...ADMIN_TIER_ROLES, "manager", "stores_manager", "stores_incharge", "maintenance_manager"];
export const VENDOR_MANAGE_ROLES = [...ADMIN_TIER_ROLES, "manager", "accounts_manager", "stores_manager"];

export const canViewFinancial = (role) => FINANCIAL_ROLES.includes(role);
export const canCreateAsset = (role) => ASSET_CREATE_ROLES.includes(role);
export const canEditMaintenance = (role) => MAINTENANCE_EDIT_ROLES.includes(role);
export const canManageUsers = (role) => USER_MGMT_ROLES.includes(role);
export const canRaisePO = (role) => PO_RAISE_ROLES.includes(role);
export const canApprovePO = (role) => PO_APPROVE_ROLES.includes(role);
export const canIssueSpare = (role) => SPARE_ISSUE_ROLES.includes(role);
export const canManageConsumables = (role) => CONSUMABLE_MANAGE_ROLES.includes(role);
export const canTransferAsset = (role) => TRANSFER_ROLES.includes(role);
export const canWriteOffAsset = (role) => WRITEOFF_ROLES.includes(role);
export const canRunAudit = (role) => AUDIT_ROLES.includes(role);

// --- Capability-based checks (Feb 2026 permissions matrix) ---
// The backend now returns `effective_capabilities` on /auth/me; we check that
// live list, falling back to the legacy role sets for older tokens.
export const hasCapability = (user, cap) => {
  if (!user || !cap) return false;
  const caps = user.effective_capabilities;
  if (Array.isArray(caps)) return caps.includes(cap);
  // Legacy fallback while the token / cached user is still on the old shape:
  const role = user.role;
  const legacyDefaults = {
    manage_users: USER_MGMT_ROLES,
    manage_company_settings: USER_MGMT_ROLES,
    manage_permissions: USER_MGMT_ROLES,
    raise_po: PO_RAISE_ROLES,
    approve_po: PO_APPROVE_ROLES,
    receive_po: PO_RAISE_ROLES,
    mark_po_issued: PO_RAISE_ROLES,
    post_po_to_books: [...PO_APPROVE_ROLES, "accounts_manager"],
    close_po: PO_APPROVE_ROLES,
    view_gst_register: FINANCIAL_ROLES,
    view_three_way_match: FINANCIAL_ROLES,
    view_financial_reports: FINANCIAL_ROLES,
    delete_asset: ["admin", "admin_addl", "manager", "purchase_manager", "admin_manager"],
    upload_asset_attachment: ["admin", "admin_addl", "manager", "purchase_manager", "admin_manager", "stores_manager", "stores_incharge", "maintenance_manager"],
    delete_asset_attachment: ["admin", "admin_addl", "manager", "purchase_manager", "admin_manager", "stores_manager", "maintenance_manager"],
    raise_consumable_request: ["admin", "admin_addl", "manager", "purchase_manager", "admin_manager", "stores_manager", "stores_incharge", "maintenance_manager", "accounts_manager", "accounts_assistant"],
    approve_consumable_request: ["admin", "admin_addl", "manager", "purchase_manager", "admin_manager", "stores_manager", "accounts_manager"],
    raise_material_transfer: ["admin", "admin_addl", "manager", "purchase_manager", "admin_manager", "stores_manager", "stores_incharge", "maintenance_manager", "accounts_manager", "accounts_assistant"],
    receive_material_transfer: ["admin", "admin_addl", "manager", "purchase_manager", "admin_manager", "stores_manager", "stores_incharge", "maintenance_manager", "accounts_manager", "accounts_assistant"],
    gate_in_material_transfer: ["admin", "admin_addl", "manager", "stores_manager", "stores_incharge", "maintenance_manager", "gate_keeper", "logistics_supervisor"],
    manage_uom_master: ["admin", "admin_addl", "manager", "purchase_manager", "admin_manager", "stores_manager"],
    // Feb 2026 — granular consumables capabilities (defaults must match backend registry).
    create_consumable: ["admin", "admin_addl", "manager", "purchase_manager", "admin_manager", "stores_manager"],
    edit_consumable_details: ["admin", "admin_addl", "manager", "purchase_manager", "admin_manager", "stores_manager"],
    adjust_consumable_stock: ["admin", "admin_addl", "manager", "admin_manager", "stores_manager", "stores_incharge"],
    manage_email_templates: ["admin", "admin_addl", "manager"],
    deploy_asset: ["admin", "admin_addl", "manager", "maintenance_manager", "stores_manager", "stores_incharge", "technician"],
    manage_deployment_masters: ["admin", "admin_addl", "manager"],
    manage_locations: ["admin", "admin_addl", "manager"],
    // Feb 2026 — People IQ Phase 1
    view_employees: ["admin", "admin_addl", "hr_manager", "manager"],
    create_employee: ["admin", "admin_addl", "hr_manager"],
    edit_employee: ["admin", "admin_addl", "hr_manager"],
    finalize_employee: ["admin", "admin_addl", "hr_manager"],
    unlock_finalized_employee: ["admin", "admin_addl", "hr_manager"],
    view_employee_sensitive: ["admin", "admin_addl", "hr_manager"],
    change_lifecycle_state: ["admin", "admin_addl", "hr_manager"],
    manage_org_structure: ["admin", "admin_addl", "hr_manager"],
    view_employee_audit: ["admin", "admin_addl", "hr_manager"],
    // Phase 2
    upload_employee_document: ["admin", "admin_addl", "hr_manager"],
    download_employee_document: ["admin", "admin_addl", "hr_manager"],
    delete_employee_document: ["admin", "admin_addl", "hr_manager"],
    manage_employee_dependents: ["admin", "admin_addl", "hr_manager"],
    approve_employee_change_request: ["admin", "admin_addl", "hr_manager"],
    manage_onboarding: ["admin", "admin_addl", "hr_manager"],
    manage_offboarding: ["admin", "admin_addl", "hr_manager"],
    manage_checklist_templates: ["admin", "admin_addl", "hr_manager"],
  };
  const set = legacyDefaults[cap];
  return set ? set.includes(role) : false;
};

export const formatINR = (n) => {
  if (n == null || isNaN(n)) return "—";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(n);
};

export const formatDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      timeZone: "Asia/Kolkata",
    });
  } catch { return d; }
};

// Full IST date + time — use anywhere a timestamp is shown (audit logs,
// signatures, sessions, gate-in / received-at, notifications, etc.).
export const formatDateTime = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", hour12: false,
      timeZone: "Asia/Kolkata",
    }) + " IST";
  } catch { return d; }
};

// Just time-of-day in IST — for compact rows where the date is already shown.
export const formatTime = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleTimeString("en-IN", {
      hour: "2-digit", minute: "2-digit", hour12: false,
      timeZone: "Asia/Kolkata",
    }) + " IST";
  } catch { return d; }
};
